import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";
import { ApiError } from "@shared-core/data/api/errors";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  IssueViewItem,
  IssueRecord,
  ActivityRecord,
  AttachmentRecord,
  IssueSaveInvestigation,
  IssueResolve,
  AddIssueNote,
} from "@shared-core/data/api-types";
import type { UserListItem } from "@shared-core/data/api/users";
import { Button } from "../app/components/primitives/button";
import { Card } from "../app/components/primitives/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../app/components/primitives/dialog";
import { Label } from "../app/components/primitives/label";
import { Textarea } from "../app/components/primitives/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../app/components/primitives/tabs";
import { ArrowLeft, AlertCircle, CheckCircle2 } from "lucide-react";
import { paths } from "@shared-core/routing/routes";
import { useSmartBack } from "../app/useSmartBack";
import { useAuth, useCanInvestigateIssues } from "../app/auth/hooks";
import { isIssueResolved } from "@shared-core/domain/issues";
import {
  IssueStatusBadge,
  IssueTypeBadge,
  IssueSourceBadge,
} from "../app/components/domain/issues/issueBadges";
import { IssueWorkingHeader } from "../app/components/domain/issues/IssueWorkingHeader";
import { LegacyIssueReportPanel } from "../app/components/domain/issues/LegacyIssueReportPanel";
import { IssueReportTab } from "../app/components/domain/issues/IssueReportTab";
import { IssueInvestigationTab } from "../app/components/domain/issues/IssueInvestigationTab";
import { IssueActivityTab } from "../app/components/domain/issues/IssueActivityTab";

export function IssueDetailPage() {
  const { id } = useParams<{ id: string }>();
  const goBack = useSmartBack(paths.issues());
  const adapter = useDataAdapter();
  const { user } = useAuth();
  const canInvestigate = useCanInvestigateIssues();

  const [viewItem, setViewItem] = useState<IssueViewItem | null>(null);
  const [issueRecord, setIssueRecord] = useState<IssueRecord | null>(null);
  const [activities, setActivities] = useState<ActivityRecord[]>([]);
  const [attachments, setAttachments] = useState<AttachmentRecord[]>([]);
  const [resolverUsers, setResolverUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("report");
  const [busy, setBusy] = useState<
    "none" | "investigation" | "resolution" | "note" | "reopen"
  >("none");
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");

  const issueId = id ?? "";

  useEffect(() => {
    if (!issueId) return;
    let cancelled = false;
    setLoading(true);

    // Fetch the view item first (works for both platform and legacy).
    adapter
      .getIssueView(issueId)
      .then(async (view) => {
        if (cancelled) return;
        setViewItem(view);

        // For platform-native issues, also fetch the full detail.
        if (view && view.source === "platform") {
          const [detail, usersResponse] = await Promise.all([
            adapter.getIssueDetail(issueId),
            adapter.listUsers(),
          ]);
          if (!cancelled) {
            if (detail) {
              setIssueRecord(detail.issue);
              setActivities(detail.activities);
              setAttachments(detail.attachments);
            }
            setResolverUsers(usersResponse.items);
          }
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [adapter, issueId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading issue…</div>
      </div>
    );
  }

  if (!viewItem) {
    return (
      <div className="space-y-6">
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Issues
        </button>
        <Card className="p-12 text-center">
          <h3 className="mb-2">Issue not found</h3>
          <p className="text-muted-foreground">
            The issue you're looking for doesn't exist.
          </p>
        </Card>
      </div>
    );
  }

  const isLegacy = viewItem.source === "legacy";
  const editable = canInvestigate && !isLegacy && !!issueRecord;
  const resolved = isIssueResolved(viewItem);

  // Working header data composed from view item + record.
  const headerIssue = {
    status: viewItem.status,
    source: viewItem.source,
    assignee_id: viewItem.assignee_id,
    assignee_name: issueRecord?.assignee_name ?? null,
    customer_id: viewItem.customer_id,
    customer_name: viewItem.customer_name,
    raised_at: viewItem.raised_at,
    raised_by_id: issueRecord?.raised_by_id ?? "",
    identified_by_id: issueRecord?.identified_by_id ?? null,
    identified_by_name: issueRecord?.identified_by_name ?? null,
    age_days: viewItem.age_days,
    related_reference: issueRecord?.related_reference ?? null,
    initial_investigation_department:
      issueRecord?.initial_investigation_department ?? null,
    resolved_at: issueRecord?.resolved_at ?? null,
    resolved_by_id: issueRecord?.resolved_by_id ?? null,
    legacy_crm_url: null,
  };

  async function handleStartInvestigation() {
    const updated = await adapter.startInvestigation(issueId);
    setViewItem((prev) => prev && { ...prev, status: updated.status });
    setIssueRecord(updated);
  }

  async function handleAssign(userId: string | null) {
    const updated = await adapter.assignIssue(issueId, { assignee_id: userId });
    setViewItem(
      (prev) => prev && { ...prev, assignee_id: updated.assignee_id },
    );
    setIssueRecord(updated);
  }

  async function handleSaveInvestigation(data: IssueSaveInvestigation) {
    setBusy("investigation");
    try {
      const updated = await adapter.saveInvestigation(issueId, data);
      setIssueRecord(updated);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.detail : "Failed to save investigation";
      toast.error(msg);
    } finally {
      setBusy("none");
    }
  }

  async function handleResolve(data: IssueResolve) {
    setBusy("resolution");
    try {
      const updated = await adapter.resolveIssue(issueId, data);
      setViewItem((prev) => prev && { ...prev, status: updated.status });
      setIssueRecord(updated);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.detail : "Failed to resolve issue";
      toast.error(msg);
    } finally {
      setBusy("none");
    }
  }

  function handleReopen() {
    setReopenReason("");
    setReopenDialogOpen(true);
  }

  async function handleReopenConfirm() {
    setBusy("reopen");
    try {
      const updated = await adapter.reopenIssue(issueId, {
        reopen_reason: reopenReason,
      });
      setViewItem((prev) => prev && { ...prev, status: updated.status });
      setIssueRecord(updated);
      setReopenDialogOpen(false);
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.detail : "Failed to reopen issue";
      toast.error(msg);
    } finally {
      setBusy("none");
    }
  }

  async function handleAddNote(data: AddIssueNote) {
    setBusy("note");
    try {
      await adapter.addIssueNote(issueId, data);
      // Re-fetch the detail to get the updated activity list.
      const detail = await adapter.getIssueDetail(issueId);
      if (detail) setActivities(detail.activities);
    } finally {
      setBusy("none");
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Issues
        </button>
        <div className="flex items-start gap-3">
          {resolved ? (
            <CheckCircle2 className="w-6 h-6 mt-1 shrink-0 text-[color:var(--color-success)]" />
          ) : (
            <AlertCircle className="w-6 h-6 mt-1 shrink-0 text-[color:var(--color-warning)]" />
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <span className="text-sm font-medium text-muted-foreground">
                {viewItem.reference}
              </span>
              <IssueStatusBadge status={viewItem.status} />
              <IssueTypeBadge type={viewItem.type_id} />
              <IssueSourceBadge source={viewItem.source} />
            </div>
            <h1 className="break-words">{viewItem.title}</h1>
          </div>
        </div>
      </div>

      <IssueWorkingHeader
        issue={headerIssue}
        canInvestigate={canInvestigate}
        currentUserId={user?.id ?? ""}
        onStartInvestigation={handleStartInvestigation}
        onAssign={handleAssign}
        onReopen={handleReopen}
      />

      {/* Legacy Implog issues have no workflow tabs — just the report content. */}
      {isLegacy ? (
        <LegacyIssueReportPanel issue={viewItem} />
      ) : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {issueRecord && <TabsTrigger value="report">Report</TabsTrigger>}
            {canInvestigate && issueRecord && (
              <TabsTrigger value="investigation">Investigation</TabsTrigger>
            )}
            <TabsTrigger value="activity">Activity</TabsTrigger>
          </TabsList>

          {issueRecord && (
            <TabsContent value="report" className="mt-4">
              <IssueReportTab issue={issueRecord} attachments={attachments} />
            </TabsContent>
          )}

          {canInvestigate && issueRecord && (
            <TabsContent value="investigation" className="mt-4">
              <IssueInvestigationTab
                issue={issueRecord}
                editable={editable}
                saving={busy === "investigation"}
                resolving={busy === "resolution"}
                resolverUsers={resolverUsers}
                onSave={handleSaveInvestigation}
                onResolve={handleResolve}
              />
            </TabsContent>
          )}

          <TabsContent value="activity" className="mt-4">
            <IssueActivityTab
              activities={activities}
              canSeeRestricted={canInvestigate}
              posting={busy === "note"}
              onAddNote={handleAddNote}
            />
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={reopenDialogOpen} onOpenChange={setReopenDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reopen issue</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reopen-reason">Reason for reopening</Label>
            <Textarea
              id="reopen-reason"
              placeholder="Explain why this issue is being reopened…"
              value={reopenReason}
              onChange={(e) => setReopenReason(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReopenDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReopenConfirm}
              disabled={!reopenReason.trim() || busy === "reopen"}
            >
              {busy === "reopen" ? "Reopening…" : "Reopen issue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
